import { useState, useEffect, useCallback } from 'react'
import {
  Title, Group, Badge, Tabs, Stack, TextInput, Textarea, Button,
  Card, Text, NumberInput, SimpleGrid, LoadingOverlay, Table,
  ActionIcon, Modal, Alert, Tooltip, Divider, Switch,
} from '@mantine/core'
import {
  IconEdit, IconTrash, IconPlus,
  IconHome2, IconInfoCircle, IconPhone, IconHelp,
  IconArrowUp, IconArrowDown, IconDeviceFloppy, IconAlertCircle,
  IconCheck, IconX, IconRefresh, IconBrandFacebook, IconBrandInstagram,
  IconMapPin, IconPhoto, IconUsers,
} from '@tabler/icons-react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { confirmAction } from '@/lib/confirm'
import type { HeroContent, AboutContent, ContactContent, FaqItem, TeamMember, SurveyContent, ReferralLetterContent } from '@/types'

// ─── Hero Tab ─────────────────────────────────────────────────────────────────

function HeroTab() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<HeroContent>({
    defaultValues: { title: '', subtitle: '', ctaText: '', ctaLink: '', backgroundImage: '', isActive: true },
  })

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    api.admin
      .getWebsiteContent()
      .then((res: any) => {
        if (res?.hero) reset(res.hero)
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Өгөгдөл ачаалахад алдаа гарлаа'
        setError(msg)
        toast.error(msg)
      })
      .finally(() => setLoading(false))
  }, [reset])

  useEffect(() => { load() }, [load])

  const onSubmit = handleSubmit(async (form) => {
    try {
      await api.admin.updateHeroContent(form as unknown as Record<string, unknown>)
      toast.success('Нүүр баннер хадгалагдлаа')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Хадгалахад алдаа гарлаа')
    }
  })

  return (
    <Stack style={{ position: 'relative' }}>
      <LoadingOverlay visible={loading} />

      {error && (
        <Alert icon={<IconAlertCircle size={18} />} title="Алдаа" color="red" variant="light">
          <Group justify="space-between" align="center">
            <Text size="sm">{error}</Text>
            <Button size="xs" variant="light" color="red" leftSection={<IconRefresh size={14} />} onClick={load}>
              Дахин оролдох
            </Button>
          </Group>
        </Alert>
      )}

      <Card shadow="xs" radius="md" withBorder p="lg">
        <Text fw={600} size="sm" mb="md" c="dimmed" tt="uppercase" lts={0.5}>
          Нүүр баннерийн агуулга
        </Text>
        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <Controller
              name="title"
              control={control}
              rules={{ validate: (v) => v.trim() ? true : 'Гарчиг шаардлагатай' }}
              render={({ field }) => (
                <TextInput label="Гарчиг *" placeholder="Байгалийн эмчилгээ" {...field} error={errors.title?.message} />
              )}
            />
            <Controller name="subtitle" control={control} render={({ field }) => (
              <Textarea label="Дэд гарчиг" placeholder="Уламжлалт монгол эмчилгээ..." autosize minRows={2} {...field} />
            )} />
            <Divider label="CTA Товч" labelPosition="left" />
            <Controller name="ctaText" control={control} render={({ field }) => (
              <TextInput label="Товчны үг" placeholder="Цаг захиалах" {...field} />
            )} />
            <Controller name="ctaLink" control={control} render={({ field }) => (
              <TextInput label="Товчны холбоос (URL)" placeholder="/booking" {...field} />
            )} />
            <Divider label="Медиа" labelPosition="left" />
            <Controller name="backgroundImage" control={control} render={({ field }) => (
              <TextInput label="Арын зургийн URL" placeholder="https://example.com/hero-bg.jpg" leftSection={<IconPhoto size={16} />} {...field} />
            )} />
            <Group>
              <Controller name="isActive" control={control} render={({ field }) => (
                <Switch label="Идэвхтэй" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              )} />
            </Group>
            <Group justify="flex-end" mt="xs">
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting} leftSection={<IconDeviceFloppy size={16} />}>
                Хадгалах
              </Button>
            </Group>
          </Stack>
        </form>
      </Card>
    </Stack>
  )
}

// ─── About Tab ────────────────────────────────────────────────────────────────

export function AboutTab() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const { control, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } = useForm<AboutContent>({
    defaultValues: {
      title: '', description: '', image: '',
      stats: { doctors: 0, experience: 0, patients: 0, services: 0 },
      mission: '', vision: '',
      foundingYear: 2020,
      showDownloads: false,
    },
  })
  const watchImage = watch('image')

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    api.admin
      .getWebsiteContent()
      .then((res: any) => {
        const a = res?.data?.about ?? res?.about
        if (a) reset({
          title: a.title || '',
          description: a.description || '',
          image: a.image || '',
          stats: a.stats || { doctors: 0, experience: 0, patients: 0, services: 0 },
          mission: a.mission || '',
          vision: a.vision || '',
          foundingYear: a.foundingYear ?? 2020,
          showDownloads: a.showDownloads ?? false,
        })
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Өгөгдөл ачаалахад алдаа гарлаа'
        setError(msg)
        toast.error(msg)
      })
      .finally(() => setLoading(false))
  }, [reset])

  useEffect(() => { load() }, [load])

  const handleImageUpload = async (file: File) => {
    setUploading(true)
    try {
      const result = await api.admin.uploadImage(file)
      setValue('image', result.url, { shouldDirty: true })
      toast.success('Зураг байршуулагдлаа')
    } catch {
      toast.error('Зураг байршуулахад алдаа гарлаа')
    } finally {
      setUploading(false)
    }
  }

  const onSubmit = handleSubmit(async (form) => {
    try {
      await api.admin.updateAboutContent(form as unknown as Record<string, unknown>)
      toast.success('Бидний тухай хэсэг хадгалагдлаа')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Хадгалахад алдаа гарлаа')
    }
  })

  return (
    <Stack style={{ position: 'relative' }}>
      <LoadingOverlay visible={loading} />

      {error && (
        <Alert icon={<IconAlertCircle size={18} />} title="Алдаа" color="red" variant="light">
          <Group justify="space-between" align="center">
            <Text size="sm">{error}</Text>
            <Button size="xs" variant="light" color="red" leftSection={<IconRefresh size={14} />} onClick={load}>
              Дахин оролдох
            </Button>
          </Group>
        </Alert>
      )}

      <Card shadow="xs" radius="md" withBorder p="lg">
        <Text fw={600} size="sm" mb="md" c="dimmed" tt="uppercase" lts={0.5}>
          Бидний тухай хэсгийн агуулга
        </Text>
        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <Controller
              name="title"
              control={control}
              rules={{ validate: (v) => v.trim() ? true : 'Гарчиг шаардлагатай' }}
              render={({ field }) => <TextInput label="Гарчиг *" placeholder="Бидний тухай" {...field} error={errors.title?.message} />}
            />
            <Controller name="description" control={control} render={({ field }) => (
              <Textarea label="Тайлбар" placeholder="Бидний тухай дэлгэрэнгүй..." autosize minRows={3} {...field} />
            )} />
            <div>
              <Text size="sm" fw={500} mb={4}>Зураг</Text>
              <Group>
                <Controller name="image" control={control} render={({ field }) => (
                  <TextInput style={{ flex: 1 }} placeholder="Зургийн URL" leftSection={<IconPhoto size={16} />} {...field} />
                )} />
                <Button
                  size="sm" variant="light" leftSection={<IconPhoto size={14} />}
                  loading={uploading}
                  onClick={() => {
                    const input = document.createElement('input')
                    input.type = 'file'
                    input.accept = 'image/*'
                    input.onchange = () => input.files?.[0] && handleImageUpload(input.files[0])
                    input.click()
                  }}
                >
                  Upload
                </Button>
              </Group>
              {watchImage && <img src={watchImage} alt="" style={{ marginTop: 8, maxHeight: 120, borderRadius: 8, objectFit: 'cover' }} />}
            </div>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Controller name="foundingYear" control={control} render={({ field }) => (
                <NumberInput
                  label="Үүсгэн байгуулагдсан он"
                  description="Нүүр хэсэгт харагдах он"
                  min={1900}
                  max={2100}
                  value={field.value}
                  onChange={(v) => field.onChange(Number(v) || 2020)}
                />
              )} />
              <Group align="flex-end" pb={4}>
                <Controller name="showDownloads" control={control} render={({ field }) => (
                  <Switch
                    label="Лого татах хэсгийг харуулах"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )} />
              </Group>
            </SimpleGrid>
            <Divider label="Эрхэм зорилго / Алсын хараа" labelPosition="left" />
            <Controller name="mission" control={control} render={({ field }) => (
              <Textarea label="Эрхэм зорилго" placeholder="Бидний эрхэм зорилго..." autosize minRows={3} {...field} />
            )} />
            <Controller name="vision" control={control} render={({ field }) => (
              <Textarea label="Алсын хараа" placeholder="Бидний алсын хараа..." autosize minRows={3} {...field} />
            )} />
            <Group justify="flex-end" mt="xs">
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting} leftSection={<IconDeviceFloppy size={16} />}>
                Хадгалах
              </Button>
            </Group>
          </Stack>
        </form>
      </Card>
    </Stack>
  )
}

// ─── Contact Tab ──────────────────────────────────────────────────────────────

export function ContactTab() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { control, handleSubmit, reset, setValue, watch, formState: { isSubmitting } } = useForm<ContactContent>({
    defaultValues: { addresses: [], phones: [], email: '', workingHours: '', mapEmbed: '', facebook: '', instagram: '' },
  })

  const addressesField = useFieldArray({ control, name: 'addresses' as never })
  const phonesField = useFieldArray({ control, name: 'phones' })

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    api.admin
      .getWebsiteContent()
      .then((res: any) => {
        const c = res?.data?.contact ?? res?.contact
        if (c) reset({
          addresses: Array.isArray(c.addresses) ? c.addresses : [],
          phones: Array.isArray(c.phones) ? c.phones : [],
          email: c.email || '',
          workingHours: c.workingHours || '',
          mapEmbed: c.mapEmbed || '',
          facebook: c.facebook || '',
          instagram: c.instagram || '',
        })
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Өгөгдөл ачаалахад алдаа гарлаа'
        setError(msg)
        toast.error(msg)
      })
      .finally(() => setLoading(false))
  }, [reset])

  useEffect(() => { load() }, [load])

  const handleMainToggle = (idx: number, checked: boolean) => {
    const phones = watch('phones') || []
    phones.forEach((_, i) => {
      setValue(`phones.${i}.isMain`, checked && i === idx)
    })
  }

  const onSubmit = handleSubmit(async (form) => {
    // Ensure at most one main: keep only the LAST main if multiple
    const phones = [...(form.phones || [])]
    const mainIdxs = phones.map((p, i) => p.isMain ? i : -1).filter(i => i >= 0)
    if (mainIdxs.length > 1) {
      const lastMain = mainIdxs[mainIdxs.length - 1]
      phones.forEach((p, i) => { p.isMain = i === lastMain })
    }
    const payload: ContactContent = { ...form, phones }
    try {
      await api.admin.updateContactContent(payload as unknown as Record<string, unknown>)
      toast.success('Холбоо барих мэдээлэл хадгалагдлаа')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Хадгалахад алдаа гарлаа')
    }
  })

  return (
    <Stack style={{ position: 'relative' }}>
      <LoadingOverlay visible={loading} />

      {error && (
        <Alert icon={<IconAlertCircle size={18} />} title="Алдаа" color="red" variant="light">
          <Group justify="space-between" align="center">
            <Text size="sm">{error}</Text>
            <Button size="xs" variant="light" color="red" leftSection={<IconRefresh size={14} />} onClick={load}>
              Дахин оролдох
            </Button>
          </Group>
        </Alert>
      )}

      <Card shadow="xs" radius="md" withBorder p="lg">
        <Text fw={600} size="sm" mb="md" c="dimmed" tt="uppercase" lts={0.5}>
          Холбоо барих мэдээлэл
        </Text>
        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <Controller name="email" control={control} render={({ field }) => (
              <TextInput label="И-мэйл хаяг" placeholder="info@naturemed.mn" {...field} />
            )} />

            <Divider label="Хаягууд" labelPosition="left" />
            <Stack gap="xs">
              {addressesField.fields.map((f, idx) => (
                <Group key={f.id} align="flex-end" gap="xs" wrap="nowrap">
                  <Controller
                    name={`addresses.${idx}` as const}
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        style={{ flex: 1 }}
                        placeholder="Хаяг"
                        leftSection={<IconMapPin size={16} />}
                        {...field}
                      />
                    )}
                  />
                  <ActionIcon color="red" variant="light" onClick={() => addressesField.remove(idx)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
              <Button
                variant="light"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => addressesField.append('' as never)}
                style={{ alignSelf: 'flex-start' }}
              >
                Хаяг нэмэх
              </Button>
            </Stack>

            <Divider label="Утасны дугаарууд" labelPosition="left" />
            <Stack gap="xs">
              {phonesField.fields.map((f, idx) => (
                <Group key={f.id} align="flex-end" gap="xs" wrap="nowrap">
                  <Controller
                    name={`phones.${idx}.number` as const}
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        style={{ flex: 1 }}
                        placeholder="+976 9900 0000"
                        leftSection={<IconPhone size={16} />}
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    name={`phones.${idx}.label` as const}
                    control={control}
                    render={({ field }) => (
                      <TextInput style={{ flex: 1 }} placeholder="Тайлбар (Захиалга, гэх мэт)" {...field} />
                    )}
                  />
                  <Controller
                    name={`phones.${idx}.isMain` as const}
                    control={control}
                    render={({ field }) => (
                      <Switch
                        label="Үндсэн"
                        checked={!!field.value}
                        onChange={(e) => handleMainToggle(idx, e.target.checked)}
                      />
                    )}
                  />
                  <ActionIcon color="red" variant="light" onClick={() => phonesField.remove(idx)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
              <Button
                variant="light"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => phonesField.append({ number: '', label: '', isMain: false })}
                style={{ alignSelf: 'flex-start' }}
              >
                Утас нэмэх
              </Button>
            </Stack>

            <Controller name="workingHours" control={control} render={({ field }) => (
              <Textarea label="Ажлын цаг" placeholder="Даваа – Баасан: 08:00 – 20:00" autosize minRows={3} {...field} />
            )} />
            <Divider label="Социал хаягууд" labelPosition="left" />
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Controller name="facebook" control={control} render={({ field }) => (
                <TextInput label="Facebook" placeholder="https://facebook.com/naturemed" leftSection={<IconBrandFacebook size={16} />} {...field} />
              )} />
              <Controller name="instagram" control={control} render={({ field }) => (
                <TextInput label="Instagram" placeholder="https://instagram.com/naturemed" leftSection={<IconBrandInstagram size={16} />} {...field} />
              )} />
            </SimpleGrid>
            <Divider label="Газрын зураг" labelPosition="left" />
            <Controller name="mapEmbed" control={control} render={({ field }) => (
              <Textarea label="Google Maps Embed код" placeholder='<iframe src="https://maps.google.com/..." ...></iframe>' autosize minRows={3} leftSection={<IconMapPin size={16} />} {...field} />
            )} />
            <Group justify="flex-end" mt="xs">
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting} leftSection={<IconDeviceFloppy size={16} />}>
                Хадгалах
              </Button>
            </Group>
          </Stack>
        </form>
      </Card>
    </Stack>
  )
}

// ─── FAQ Tab ──────────────────────────────────────────────────────────────────

interface FaqFormData {
  question: string
  answer: string
  order: number
  isActive: boolean
}

const defaultFaq: FaqFormData = { question: '', answer: '', order: 0, isActive: true }

export function FaqTab() {
  const [items, setItems] = useState<FaqItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [togglingId, setTogglingId] = useState<string | null>(null)

  const [orderChanged, setOrderChanged] = useState(false)
  const [reordering, setReordering] = useState(false)

  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<FaqFormData>({ defaultValues: defaultFaq })

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    setOrderChanged(false)
    api.admin
      .getFaqs()
      .then((res: any) => {
        const data: FaqItem[] = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : []
        setItems([...data].sort((a, b) => a.order - b.order))
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'FAQ ачаалахад алдаа гарлаа'
        setError(msg)
        toast.error(msg)
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const moveUp = (index: number) => {
    if (index === 0) return
    setItems((prev) => { const next = [...prev];[next[index - 1], next[index]] = [next[index], next[index - 1]]; return next })
    setOrderChanged(true)
  }
  const moveDown = (index: number) => {
    if (index === items.length - 1) return
    setItems((prev) => { const next = [...prev];[next[index], next[index + 1]] = [next[index + 1], next[index]]; return next })
    setOrderChanged(true)
  }
  const saveOrder = () => confirmAction('Шинэ дарааллыг хадгалах уу? Энэ нь website дээр харагдах дарааллыг өөрчилнө.', async () => {
    setReordering(true)
    try {
      await api.admin.reorderFaqs(items.map((item) => item._id))
      toast.success('FAQ-н дараалал хадгалагдлаа')
      setOrderChanged(false)
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Дараалал хадгалахад алдаа гарлаа')
    } finally { setReordering(false) }
  }, { title: 'Дараалал хадгалах', confirmLabel: 'Хадгалах' })

  const openCreate = () => {
    setEditingId(null)
    reset({ ...defaultFaq, order: items.length })
    setModalOpen(true)
  }
  const openEdit = (item: FaqItem) => {
    setEditingId(item._id)
    reset({
      question: item.question || '',
      answer: item.answer || '',
      order: item.order ?? 0,
      isActive: item.isActive ?? true,
    })
    setModalOpen(true)
  }
  const closeModal = () => {
    setModalOpen(false)
    reset(defaultFaq)
  }

  const onSubmit = handleSubmit(async (form) => {
    try {
      const payload: Record<string, unknown> = {
        question: form.question,
        answer: form.answer,
        order: Number(form.order),
        isActive: form.isActive,
      }
      if (editingId) {
        await api.admin.updateFaq(editingId, payload)
        toast.success('FAQ шинэчлэгдлээ')
      } else {
        await api.admin.createFaq(payload)
        toast.success('FAQ нэмэгдлээ')
      }
      closeModal()
      load()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Хадгалахад алдаа гарлаа')
    }
  })

  const handleToggleStatus = async (item: FaqItem) => {
    setTogglingId(item._id)
    try {
      await api.admin.updateFaqStatus(item._id, !item.isActive)
      toast.success(`FAQ ${!item.isActive ? 'идэвхжлээ' : 'идэвхгүй боллоо'}`)
      load()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Төлөв өөрчлөхөд алдаа гарлаа')
    } finally { setTogglingId(null) }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.admin.deleteFaq(deleteId)
      toast.success('FAQ устгагдлаа')
      load()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Устгахад алдаа гарлаа')
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  return (
    <div>
      <Group justify="space-between" mb="sm">
        <Stack gap={2}>
          <Text fw={500} size="sm">Асуулт хариултын жагсаалт</Text>
          <Text size="xs" c="dimmed">↑ ↓ товч ашиглан дараалал өөрчилж "Дараалал хадгалах" дарна уу.</Text>
        </Stack>
        <Group gap="xs">
          {orderChanged && (
            <Button variant="light" color="orange" leftSection={<IconDeviceFloppy size={16} />} onClick={saveOrder} loading={reordering}>
              Дараалал хадгалах
            </Button>
          )}
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>FAQ нэмэх</Button>
        </Group>
      </Group>

      {orderChanged && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light" mb="md">
          Дараалал өөрчлөгдсөн байна. "Дараалал хадгалах" товч дарж хадгална уу.
        </Alert>
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={18} />} title="Алдаа" color="red" variant="light" mb="md">
          <Group justify="space-between" align="center">
            <Text size="sm">{error}</Text>
            <Button size="xs" variant="light" color="red" leftSection={<IconRefresh size={14} />} onClick={load}>Дахин оролдох</Button>
          </Group>
        </Alert>
      )}

      <Card shadow="xs" padding={0} radius="md" withBorder style={{ position: 'relative' }}>
        <LoadingOverlay visible={loading} />
        <Table.ScrollContainer minWidth={700}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--app-table-header)' }}>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={80}>Дараалал</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Асуулт</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Хариулт</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={100}>Төлөв</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={120}>Үйлдэл</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((item, i) => (
                <Table.Tr key={item._id}>
                  <Table.Td>
                    <Group gap={2} wrap="nowrap">
                      <Tooltip label="Дээш" position="top" withArrow>
                        <ActionIcon variant="subtle" color="gray" size="xs" radius="sm" disabled={i === 0} onClick={() => moveUp(i)}>
                          <IconArrowUp size={13} />
                        </ActionIcon>
                      </Tooltip>
                      <Text size="xs" c="dimmed" w={20} ta="center">{i + 1}</Text>
                      <Tooltip label="Доош" position="top" withArrow>
                        <ActionIcon variant="subtle" color="gray" size="xs" radius="sm" disabled={i === items.length - 1} onClick={() => moveDown(i)}>
                          <IconArrowDown size={13} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                  <Table.Td><Text size="sm" fw={500} lineClamp={2}>{item.question}</Text></Table.Td>
                  <Table.Td><Text size="sm" lineClamp={2} c="dimmed">{item.answer}</Text></Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={item.isActive ? 'green' : 'gray'} size="sm">
                      {item.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Tooltip label={item.isActive ? 'Идэвхгүй болгох' : 'Идэвхжүүлэх'} withArrow>
                        <ActionIcon variant="light" color={item.isActive ? 'gray' : 'green'} radius="md" loading={togglingId === item._id} onClick={() => handleToggleStatus(item)}>
                          {item.isActive ? <IconX size={16} /> : <IconCheck size={16} />}
                        </ActionIcon>
                      </Tooltip>
                      <ActionIcon variant="light" color="blue" radius="md" onClick={() => openEdit(item)}>
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="red" radius="md" onClick={() => setDeleteId(item._id)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {!loading && !error && items.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}><Text ta="center" c="dimmed" py="xl">FAQ байхгүй байна</Text></Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      {/* Delete Modal */}
      <Modal opened={!!deleteId} onClose={() => setDeleteId(null)} title="FAQ устгах" centered size="sm">
        <Stack>
          <Text size="sm">Энэ асуулт хариултыг устгахдаа итгэлтэй байна уу?</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteId(null)}>Болих</Button>
            <Button color="red" onClick={handleDelete} loading={deleting} disabled={deleting}>Устгах</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={closeModal} title={editingId ? 'FAQ засах' : 'Шинэ FAQ нэмэх'} centered size="xl">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller
              name="question"
              control={control}
              rules={{ validate: (v) => v.trim() ? true : 'Асуулт шаардлагатай' }}
              render={({ field }) => (
                <Textarea label="Асуулт *" placeholder="Захиалга хэрхэн хийх вэ?" autosize minRows={2} {...field} error={errors.question?.message} />
              )}
            />
            <Controller
              name="answer"
              control={control}
              rules={{ validate: (v) => v.trim() ? true : 'Хариулт шаардлагатай' }}
              render={({ field }) => (
                <Textarea label="Хариулт *" placeholder="Захиалга хийхдээ..." autosize minRows={3} {...field} error={errors.answer?.message} />
              )}
            />
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Controller name="order" control={control} render={({ field }) => (
                <NumberInput label="Дараалал" min={0} value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} />
              )} />
              <Group align="flex-end" pb={4}>
                <Controller name="isActive" control={control} render={({ field }) => (
                  <Switch label="Идэвхтэй" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                )} />
              </Group>
            </SimpleGrid>
            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={closeModal} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {editingId ? 'Хадгалах' : 'Нэмэх'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </div>
  )
}

// ─── Team Tab ─────────────────────────────────────────────────────────────────

interface TeamFormData {
  name: string
  role: string
  bio: string
  image: string
  order: number
  isActive: boolean
}

const defaultTeam: TeamFormData = { name: '', role: '', bio: '', image: '', order: 0, isActive: true }

function TeamTab() {
  const [items, setItems] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [togglingId, setTogglingId] = useState<string | null>(null)

  const [orderChanged, setOrderChanged] = useState(false)
  const [reordering, setReordering] = useState(false)

  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<TeamFormData>({ defaultValues: defaultTeam })

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    setOrderChanged(false)
    api.admin
      .getTeamMembers()
      .then((res: any) => {
        const data: TeamMember[] = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []
        setItems([...data].sort((a, b) => a.order - b.order))
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Баг ачаалахад алдаа гарлаа'
        setError(msg)
        toast.error(msg)
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const moveUp = (index: number) => {
    if (index === 0) return
    setItems((prev) => { const next = [...prev];[next[index - 1], next[index]] = [next[index], next[index - 1]]; return next })
    setOrderChanged(true)
  }
  const moveDown = (index: number) => {
    if (index === items.length - 1) return
    setItems((prev) => { const next = [...prev];[next[index], next[index + 1]] = [next[index + 1], next[index]]; return next })
    setOrderChanged(true)
  }
  const saveOrder = () => confirmAction('Шинэ дарааллыг хадгалах уу? Энэ нь website дээр харагдах дарааллыг өөрчилнө.', async () => {
    setReordering(true)
    try {
      await api.admin.reorderTeamMembers(items.map((item) => item._id))
      toast.success('Багийн дараалал хадгалагдлаа')
      setOrderChanged(false)
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Дараалал хадгалахад алдаа гарлаа')
    } finally { setReordering(false) }
  }, { title: 'Дараалал хадгалах', confirmLabel: 'Хадгалах' })

  const openCreate = () => {
    setEditingId(null)
    reset({ ...defaultTeam, order: items.length })
    setModalOpen(true)
  }
  const openEdit = (item: TeamMember) => {
    setEditingId(item._id)
    reset({
      name: item.name,
      role: item.role,
      bio: item.bio || '',
      image: item.image || '',
      order: item.order,
      isActive: item.isActive,
    })
    setModalOpen(true)
  }
  const closeModal = () => {
    setModalOpen(false)
    reset(defaultTeam)
  }

  const onSubmit = handleSubmit(async (form) => {
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        role: form.role,
        order: Number(form.order),
        isActive: form.isActive,
      }
      if (form.bio) payload.bio = form.bio
      if (form.image) payload.image = form.image
      if (editingId) {
        await api.admin.updateTeamMember(editingId, payload)
        toast.success('Гишүүн шинэчлэгдлээ')
      } else {
        await api.admin.createTeamMember(payload)
        toast.success('Гишүүн нэмэгдлээ')
      }
      closeModal()
      load()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Хадгалахад алдаа гарлаа')
    }
  })

  const handleToggleStatus = async (item: TeamMember) => {
    setTogglingId(item._id)
    try {
      await api.admin.updateTeamMemberStatus(item._id, !item.isActive)
      toast.success(`Гишүүн ${!item.isActive ? 'идэвхжлээ' : 'идэвхгүй боллоо'}`)
      load()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Төлөв өөрчлөхөд алдаа гарлаа')
    } finally { setTogglingId(null) }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.admin.deleteTeamMember(deleteId)
      toast.success('Гишүүн устгагдлаа')
      load()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Устгахад алдаа гарлаа')
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  return (
    <div>
      <Group justify="space-between" mb="sm">
        <Stack gap={2}>
          <Text fw={500} size="sm">Багийн гишүүдийн жагсаалт</Text>
          <Text size="xs" c="dimmed">↑ ↓ товч ашиглан дараалал өөрчилж "Дараалал хадгалах" дарна уу.</Text>
        </Stack>
        <Group gap="xs">
          {orderChanged && (
            <Button variant="light" color="orange" leftSection={<IconDeviceFloppy size={16} />} onClick={saveOrder} loading={reordering}>
              Дараалал хадгалах
            </Button>
          )}
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Гишүүн нэмэх</Button>
        </Group>
      </Group>

      {orderChanged && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light" mb="md">
          Дараалал өөрчлөгдсөн байна. "Дараалал хадгалах" товч дарж хадгална уу.
        </Alert>
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={18} />} title="Алдаа" color="red" variant="light" mb="md">
          <Group justify="space-between" align="center">
            <Text size="sm">{error}</Text>
            <Button size="xs" variant="light" color="red" leftSection={<IconRefresh size={14} />} onClick={load}>Дахин оролдох</Button>
          </Group>
        </Alert>
      )}

      <Card shadow="xs" padding={0} radius="md" withBorder style={{ position: 'relative' }}>
        <LoadingOverlay visible={loading} />
        <Table.ScrollContainer minWidth={700}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--app-table-header)' }}>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={80}>Дараалал</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Нэр</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үүрэг / Мэргэжил</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Товч танилцуулга</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={100}>Төлөв</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={120}>Үйлдэл</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((item, i) => (
                <Table.Tr key={item._id}>
                  <Table.Td>
                    <Group gap={2} wrap="nowrap">
                      <Tooltip label="Дээш" position="top" withArrow>
                        <ActionIcon variant="subtle" color="gray" size="xs" radius="sm" disabled={i === 0} onClick={() => moveUp(i)}>
                          <IconArrowUp size={13} />
                        </ActionIcon>
                      </Tooltip>
                      <Text size="xs" c="dimmed" w={20} ta="center">{i + 1}</Text>
                      <Tooltip label="Доош" position="top" withArrow>
                        <ActionIcon variant="subtle" color="gray" size="xs" radius="sm" disabled={i === items.length - 1} onClick={() => moveDown(i)}>
                          <IconArrowDown size={13} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                  <Table.Td><Text size="sm" fw={500} lineClamp={1}>{item.name}</Text></Table.Td>
                  <Table.Td><Text size="sm" lineClamp={1}>{item.role}</Text></Table.Td>
                  <Table.Td><Text size="sm" lineClamp={2} c="dimmed">{item.bio || '—'}</Text></Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={item.isActive ? 'green' : 'gray'} size="sm">
                      {item.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Tooltip label={item.isActive ? 'Идэвхгүй болгох' : 'Идэвхжүүлэх'} withArrow>
                        <ActionIcon variant="light" color={item.isActive ? 'gray' : 'green'} radius="md" loading={togglingId === item._id} onClick={() => handleToggleStatus(item)}>
                          {item.isActive ? <IconX size={16} /> : <IconCheck size={16} />}
                        </ActionIcon>
                      </Tooltip>
                      <ActionIcon variant="light" color="blue" radius="md" onClick={() => openEdit(item)}>
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="red" radius="md" onClick={() => setDeleteId(item._id)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {!loading && !error && items.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}><Text ta="center" c="dimmed" py="xl">Багийн гишүүн байхгүй байна</Text></Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      {/* Delete Modal */}
      <Modal opened={!!deleteId} onClose={() => setDeleteId(null)} title="Гишүүн устгах" centered size="sm">
        <Stack>
          <Text size="sm">Энэ гишүүнийг устгахдаа итгэлтэй байна уу?</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteId(null)}>Болих</Button>
            <Button color="red" onClick={handleDelete} loading={deleting} disabled={deleting}>Устгах</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={closeModal} title={editingId ? 'Гишүүн засах' : 'Шинэ гишүүн нэмэх'} centered size="xl">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller
              name="name"
              control={control}
              rules={{ validate: (v) => v.trim() ? true : 'Нэр шаардлагатай' }}
              render={({ field }) => (
                <TextInput label="Нэр *" placeholder="Батболд Дорж" {...field} error={errors.name?.message} />
              )}
            />
            <Controller
              name="role"
              control={control}
              rules={{ validate: (v) => v.trim() ? true : 'Үүрэг шаардлагатай' }}
              render={({ field }) => (
                <TextInput label="Үүрэг / Мэргэжил *" placeholder="Мэргэжлийн эмч" {...field} error={errors.role?.message} />
              )}
            />
            <Controller name="bio" control={control} render={({ field }) => (
              <Textarea label="Товч танилцуулга" placeholder="10 жилийн туршлагатай..." autosize minRows={3} {...field} />
            )} />
            <Controller name="image" control={control} render={({ field }) => (
              <TextInput label="Зургийн URL" placeholder="https://example.com/doctor.jpg" leftSection={<IconPhoto size={16} />} {...field} />
            )} />
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Controller name="order" control={control} render={({ field }) => (
                <NumberInput label="Дараалал" min={0} value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} />
              )} />
              <Group align="flex-end" pb={4}>
                <Controller name="isActive" control={control} render={({ field }) => (
                  <Switch label="Идэвхтэй" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                )} />
              </Group>
            </SimpleGrid>
            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={closeModal} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {editingId ? 'Хадгалах' : 'Нэмэх'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </div>
  )
}

// ─── Survey Tab ──────────────────────────────────────────────────────────────

export function SurveyTab() {
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)

  const { control, handleSubmit, reset, setValue, watch, formState: { isSubmitting } } = useForm<SurveyContent>({
    defaultValues: { googleFormUrl: '', qrImage: '', title: '', description: '' },
  })
  const watchQr = watch('qrImage')

  const load = useCallback(() => {
    setLoading(true)
    api.admin.getWebsiteContent()
      .then((res: any) => {
        const s = res?.data?.survey ?? res?.survey
        if (s) reset({
          googleFormUrl: s.googleFormUrl || '',
          qrImage: s.qrImage || '',
          title: s.title || '',
          description: s.description || '',
        })
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [reset])

  useEffect(() => { load() }, [load])

  const handleUpload = async (file: File) => {
    setUploading(true)
    try {
      const r = await api.admin.uploadImage(file)
      setValue('qrImage', r.url, { shouldDirty: true })
      toast.success('QR зураг байршуулагдлаа')
    } catch { toast.error('Зураг алдаа') }
    finally { setUploading(false) }
  }

  const onSubmit = handleSubmit(async (form) => {
    try {
      await api.admin.updateSurveyContent(form as unknown as Record<string, unknown>)
      toast.success('Судалгааны тохиргоо хадгалагдлаа')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Хадгалахад алдаа гарлаа')
    }
  })

  return (
    <Stack style={{ position: 'relative' }}>
      <LoadingOverlay visible={loading} />
      <Card shadow="xs" radius="md" withBorder p="lg">
        <Text fw={600} size="sm" mb="md" c="dimmed" tt="uppercase" lts={0.5}>Сэтгэл ханамжийн судалгаа</Text>
        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <Controller name="title" control={control} render={({ field }) => (
              <TextInput label="Гарчиг" placeholder="Сэтгэл ханамжийн судалгаа" {...field} />
            )} />
            <Controller name="description" control={control} render={({ field }) => (
              <Textarea label="Тайлбар" placeholder="Таны санал бидний үйлчилгээг сайжруулахад тусална..." autosize minRows={2} {...field} />
            )} />
            <Controller name="googleFormUrl" control={control} render={({ field }) => (
              <TextInput label="Google Form URL" placeholder="https://docs.google.com/forms/d/e/..." {...field} />
            )} />
            <div>
              <Text size="sm" fw={500} mb={4}>QR код зураг</Text>
              <Group>
                <Controller name="qrImage" control={control} render={({ field }) => (
                  <TextInput style={{ flex: 1 }} placeholder="QR кодын URL" leftSection={<IconPhoto size={16} />} {...field} />
                )} />
                <Button size="sm" variant="light" leftSection={<IconPhoto size={14} />} loading={uploading} onClick={() => {
                  const input = document.createElement('input')
                  input.type = 'file'; input.accept = 'image/*'
                  input.onchange = () => input.files?.[0] && handleUpload(input.files[0])
                  input.click()
                }}>Upload</Button>
              </Group>
              {watchQr && <img src={watchQr} alt="QR" style={{ marginTop: 8, maxHeight: 200, borderRadius: 8 }} />}
            </div>
            <Group justify="flex-end" mt="xs">
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting} leftSection={<IconDeviceFloppy size={16} />}>Хадгалах</Button>
            </Group>
          </Stack>
        </form>
      </Card>
    </Stack>
  )
}

// ─── Referral Letter Tab ─────────────────────────────────────────────────────

export function ReferralLetterTab() {
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)

  const { control, handleSubmit, reset, setValue, watch, formState: { isSubmitting } } = useForm<ReferralLetterContent>({
    defaultValues: { title: '', description: '', fileUrl: '' },
  })
  const watchFileUrl = watch('fileUrl')

  const load = useCallback(() => {
    setLoading(true)
    api.admin.getWebsiteContent()
      .then((res: any) => {
        const r = res?.data?.referralLetter ?? res?.referralLetter
        if (r) reset({
          title: r.title || '',
          description: r.description || '',
          fileUrl: r.fileUrl || '',
        })
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [reset])

  useEffect(() => { load() }, [load])

  const onSubmit = handleSubmit(async (form) => {
    try {
      await api.admin.updateReferralLetterContent(form as unknown as Record<string, unknown>)
      toast.success('Илгээх бичгийн тохиргоо хадгалагдлаа')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Хадгалахад алдаа гарлаа')
    }
  })

  return (
    <Stack style={{ position: 'relative' }}>
      <LoadingOverlay visible={loading} />
      <Card shadow="xs" radius="md" withBorder p="lg">
        <Text fw={600} size="sm" mb="md" c="dimmed" tt="uppercase" lts={0.5}>Илгээх бичиг (Тайлан татах)</Text>
        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <Controller name="title" control={control} render={({ field }) => (
              <TextInput label="Гарчиг" placeholder="Илгээх бичиг" {...field} />
            )} />
            <Controller name="description" control={control} render={({ field }) => (
              <Textarea label="Тайлбар" placeholder="Илгээх бичгийн маягтын тухай..." autosize minRows={3} {...field} />
            )} />
            <div>
              <Text size="sm" fw={500} mb={4}>Файл (PDF)</Text>
              <Group>
                <Controller name="fileUrl" control={control} render={({ field }) => (
                  <TextInput style={{ flex: 1 }} placeholder="/files/ilgeeh-bichig.pdf" {...field} />
                )} />
                <Button
                  size="sm" variant="light" leftSection={<IconPhoto size={14} />}
                  loading={uploading}
                  onClick={() => {
                    const input = document.createElement('input')
                    input.type = 'file'
                    input.accept = '.pdf,application/pdf'
                    input.onchange = async () => {
                      if (!input.files?.[0]) return
                      setUploading(true)
                      try {
                        const result = await api.admin.uploadFile(input.files[0])
                        setValue('fileUrl', result.url, { shouldDirty: true })
                        toast.success('Файл байршуулагдлаа')
                      } catch {
                        toast.error('Файл байршуулахад алдаа гарлаа')
                      } finally {
                        setUploading(false)
                      }
                    }
                    input.click()
                  }}
                >
                  Upload
                </Button>
              </Group>
              {watchFileUrl && (
                <Text size="xs" mt={4} c="dimmed">
                  {watchFileUrl}
                </Text>
              )}
            </div>
            <Group justify="flex-end" mt="xs">
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting} leftSection={<IconDeviceFloppy size={16} />}>Хадгалах</Button>
            </Group>
          </Stack>
        </form>
      </Card>
    </Stack>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function WebsitePage() {
  return (
    <div>
      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <Title order={2}>Вэб хуудас</Title>
          <Badge variant="light" color="blue" size="sm">Удирдлага</Badge>
        </Group>
      </Group>

      <Tabs defaultValue="hero" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="hero" leftSection={<IconHome2 size={16} />}>Нүүр баннер</Tabs.Tab>
          <Tabs.Tab value="about" leftSection={<IconInfoCircle size={16} />}>Бидний тухай</Tabs.Tab>
          <Tabs.Tab value="team" leftSection={<IconUsers size={16} />}>Баг</Tabs.Tab>
          <Tabs.Tab value="contact" leftSection={<IconPhone size={16} />}>Холбоо барих</Tabs.Tab>
          <Tabs.Tab value="faq" leftSection={<IconHelp size={16} />}>Асуулт хариулт</Tabs.Tab>
          <Tabs.Tab value="survey" leftSection={<IconHelp size={16} />}>Судалгаа</Tabs.Tab>
          <Tabs.Tab value="referral" leftSection={<IconEdit size={16} />}>Илгээх бичиг</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="hero"><HeroTab /></Tabs.Panel>
        <Tabs.Panel value="about"><AboutTab /></Tabs.Panel>
        <Tabs.Panel value="team"><TeamTab /></Tabs.Panel>
        <Tabs.Panel value="contact"><ContactTab /></Tabs.Panel>
        <Tabs.Panel value="faq"><FaqTab /></Tabs.Panel>
        <Tabs.Panel value="survey"><SurveyTab /></Tabs.Panel>
        <Tabs.Panel value="referral"><ReferralLetterTab /></Tabs.Panel>
      </Tabs>
    </div>
  )
}
